import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { useTranslation } from '../contexts/TranslationContext';
import { Reveal, PageHero } from './LandingShared';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';

const Section = ({ title, children, delay = 0 }) => (
  <Reveal delay={delay}>
    <div className="mb-8">
      <div className="bg-[#1e3a5f] text-white px-5 py-3 rounded-t-lg">
        <h2 className="text-base sm:text-lg font-bold uppercase tracking-wide">{title}</h2>
      </div>
      <div className="bg-gray-50 border border-gray-200 border-t-0 rounded-b-lg px-5 sm:px-8 py-6">
        {children}
      </div>
    </div>
  </Reveal>
);

const DataProtection = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Helmet>
        <title>Privacy Policy — Cogent Platform</title>
        <meta name="description" content="Cogent Asset Management's privacy policy. We are committed to safeguarding your personal information in compliance with the Malaysia Personal Data Protection Act 2010 (PDPA)." />
        <link rel="canonical" href="https://cogentplatform.com/privacy-policy" />
      </Helmet>
      <Navbar />

      <main className="flex-grow">
        <PageHero title={t('dataProtection.title')} />

        <div className="bg-gradient-to-b from-white to-slate-50 py-16 sm:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
                <div className="h-1 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-t-2xl" />

                <div className="p-6 sm:p-10">
                  {/* Header */}
                  <div className="text-center mb-10">
                    <div className="flex justify-center mb-5">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
                        <LockClosedIcon className="w-7 h-7 text-white" />
                      </div>
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{t('dataProtection.title').toUpperCase()}</h1>
                    <p className="text-sm text-gray-500">{t('dataProtection.subtitle')}</p>
                  </div>

                  <div className="prose prose-sm sm:prose-base max-w-none text-gray-700 leading-7 text-justify">
                    {/* Our Legal Entities */}
                    <Section title={t('dataProtection.legalEntitiesTitle')} delay={0}>
                      <p className="mb-3">{t('dataProtection.legalEntities1')}</p>
                      <p className="mb-3">{t('dataProtection.legalEntities2')}</p>
                      <p>{t('dataProtection.legalEntities3')}</p>
                    </Section>

                    {/* Your Data That We Collect */}
                    <Section title={t('dataProtection.dataCollectTitle')} delay={50}>
                      <p>{t('dataProtection.dataCollect')}</p>
                    </Section>

                    {/* Why Do We Need Your Data */}
                    <Section title={t('dataProtection.whyNeedTitle')} delay={100}>
                      <p className="mb-3">{t('dataProtection.whyNeed1')}</p>
                      <p className="mb-3">{t('dataProtection.whyNeed2')}</p>
                      <p>{t('dataProtection.whyNeed3')}</p>
                    </Section>

                    {/* Who Provides Your Data */}
                    <Section title={t('dataProtection.whoProvideTitle')} delay={150}>
                      <p>{t('dataProtection.whoProvide')}</p>
                    </Section>

                    {/* Who Has Access to Your Data */}
                    <Section title={t('dataProtection.whoAccessTitle')} delay={200}>
                      <p className="mb-3">{t('dataProtection.whoAccess1')}</p>
                      <p className="mb-3">{t('dataProtection.whoAccess2')}</p>
                      <p>{t('dataProtection.whoAccess3')}</p>
                    </Section>

                    {/* How Long Will We Keep Your Data */}
                    <Section title={t('dataProtection.howLongTitle')} delay={250}>
                      <p>{t('dataProtection.howLong')}</p>
                    </Section>

                    {/* Know Your Rights */}
                    <Section title={t('dataProtection.rightsTitle')} delay={300}>
                      <p className="mb-4">{t('dataProtection.rightsIntro')}</p>
                      <ul className="list-disc pl-5 space-y-3 text-gray-700">
                        <li>{t('dataProtection.right1')}</li>
                        <li>{t('dataProtection.right2')}</li>
                        <li>{t('dataProtection.right3')}</li>
                        <li>{t('dataProtection.right4')}</li>
                        <li>{t('dataProtection.right5')}</li>
                        <li>{t('dataProtection.right6')}</li>
                      </ul>
                    </Section>

                    {/* Complaints Procedure */}
                    <Section title={t('dataProtection.complaintsTitle')} delay={350}>
                      <p className="mb-4">{t('dataProtection.complaints')}</p>
                      <p className="text-sm text-gray-600 font-medium">{t('dataProtection.registeredAddress')}</p>
                    </Section>
                  </div>

                  {/* Footer */}
                  <div className="mt-8 pt-6 border-t border-gray-200 text-center text-xs text-gray-400">
                    <p>&copy; {t('dataProtection.copyright')} | {t('dataProtection.lastUpdated')} | {t('dataProtection.title')}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DataProtection;
